import { h, setChildren } from "../core/dom.js";
import { api } from "../api.js";
import { navigate } from "../core/router.js";
import { icon } from "../lib/icons.js";
import { topbar, backLink, exerciseThumb, supersetLabels } from "../components/common.js";
import { MuscleMap } from "../components/MuscleMap.js";
import { openExercisePicker } from "../components/ExercisePicker.js";
import { openExerciseDetail } from "../components/exerciseDetailModal.js";
import { spinner, toast } from "../components/ui.js";
import { toDisplayWeight, toStorageKg, weightLabel, fmtTimeShort, parseTimeInput,
         toDisplayDistance, toStorageDistanceM, distanceUnit } from "../lib/format.js";

// Which target inputs a template exercise shows, mirroring the live logger's metric columns.
const metricOf = (mt) => (["weight_reps", "bodyweight_reps", "duration", "distance_duration"].includes(mt) ? mt : "weight_reps");

export default function WorkoutEditor(params) {
  const root = h("div", { class: "screen" });
  const isEdit = !!params.id;
  let name = "";
  let notes = "";
  let selected = []; // working model

  init();

  async function init() {
    setChildren(root, spinner());
    if (isEdit) {
      try {
        const w = await api.workout(params.id);
        name = w.name;
        notes = w.notes || "";
        selected = w.exercises.map(fromTemplate);
      } catch (e) {
        toast(e.message, "error");
        return navigate("/workouts");
      }
    } else {
      const prefill = sessionStorage.getItem("wizard_prefill");
      if (prefill) {
        sessionStorage.removeItem("wizard_prefill");
        try {
          const data = JSON.parse(prefill);
          name = data.name || "";
          selected = (data.exercises || []).map(fromTemplate);
        } catch {}
      }
    }
    render();
  }

  function fromTemplate(e) {
    return {
      library_exercise_id: e.library_exercise_id ?? e.id,
      exercise_name: e.exercise_name,
      images: e.images || [],
      metric_type: metricOf(e.metric_type),
      primary_muscle_ids: e.primary_muscle_ids || [],
      secondary_muscle_ids: e.secondary_muscle_ids || [],
      target_sets: e.target_sets ?? 3,
      target_reps_min: e.target_reps_min ?? 8,
      target_reps_max: e.target_reps_max ?? 12,
      target_weight_kg: e.target_weight_kg ?? null,
      target_duration_seconds: e.target_duration_seconds ?? null,
      target_distance_m: e.target_distance_m ?? null,
      rest_seconds: e.rest_seconds ?? 90,
      video_url: e.video_url ?? "",
      superset_group: e.superset_group ?? null,
    };
  }

  // Link exercise `index` into the superset of the one above it (or break the link).
  function toggleChain(index) {
    if (index <= 0) return;
    const ex = selected[index];
    const prev = selected[index - 1];
    const linked = ex.superset_group != null && ex.superset_group === prev.superset_group;
    if (linked) {
      ex.superset_group = null;
    } else {
      const gid = prev.superset_group != null ? prev.superset_group : nextGroupId();
      prev.superset_group = gid;
      ex.superset_group = gid;
    }
    render();
  }

  function nextGroupId() {
    return selected.reduce((m, e) => Math.max(m, e.superset_group || 0), 0) + 1;
  }

  function render() {
    const primary = selected.flatMap((e) => e.primary_muscle_ids);
    const secondary = selected.flatMap((e) => e.secondary_muscle_ids);

    setChildren(root, 
      backLink("Workouts", "/workouts"),
      topbar(isEdit ? "Edit workout" : "New workout"),
      h("div", { class: "field" }, h("label", null, "Name"), h("input", { class: "input", value: name, placeholder: "e.g. Push Day", oninput: (e) => (name = e.target.value) })),
      h("div", { class: "field" }, h("label", null, "Notes (optional)"), h("textarea", { class: "textarea", oninput: (e) => (notes = e.target.value) }, notes)),

      selected.length ? h("div", { class: "card", style: { padding: "12px" } }, MuscleMap({ primary, secondary })) : null,

      h("div", { class: "section-title" }, `Exercises (${selected.length})`),
      h("div", { id: "sel-list" }, ...(() => { const labels = supersetLabels(selected); return selected.map((ex, i) => exItem(ex, i, labels[i])); })()),
      h("button", { class: "btn btn-ghost btn-block mt", onClick: addExercises }, icon("plus", { size: 18 }), "Add exercises"),

      h("div", { class: "btn-row mt-lg" },
        h("button", { class: "btn btn-ghost grow", onClick: () => navigate(isEdit ? "/workouts" : "/workouts") }, "Cancel"),
        h("button", { class: "btn btn-primary grow", onClick: save }, isEdit ? "Save changes" : "Create workout")
      )
    );
  }

  function exItem(ex, index, ssLabel) {
    const linkedAbove = index > 0 && ex.superset_group != null && ex.superset_group === selected[index - 1].superset_group;
    return h(
      "div",
      { class: ["card", ssLabel && "ss-member"], style: { marginBottom: "10px", padding: "12px" } },
      h(
        "div",
        { class: "hstack" },
        ssLabel ? h("span", { class: "ss-badge", title: "Superset" }, ssLabel.label) : null,
        h(
          "div",
          { class: "hstack grow card-tap", style: { minWidth: 0 }, onClick: () => openExerciseDetail(ex.library_exercise_id), title: "View exercise" },
          exerciseThumb(ex.images, { size: 40 }),
          h("div", { class: "ex-name grow" }, ex.exercise_name),
          icon("info", { size: 15, className: "icon muted" })
        ),
        h("button", { class: "icon-btn ghost", "aria-label": `Move ${ex.exercise_name} up`, disabled: index === 0, onClick: () => move(index, -1) }, icon("back", { size: 16, className: "icon" })),
        h("button", { class: "icon-btn ghost", "aria-label": `Move ${ex.exercise_name} down`, disabled: index === selected.length - 1, onClick: () => move(index, 1) }, icon("forward", { size: 16, className: "icon" })),
        h("button", { class: "icon-btn ghost danger", "aria-label": `Remove ${ex.exercise_name}`, onClick: () => { selected.splice(index, 1); render(); } }, icon("trash", { size: 16 }))
      ),
      index > 0
        ? h("button", { class: ["ss-chain", linkedAbove && "on"], style: { marginTop: "8px" }, onClick: () => toggleChain(index) },
            icon("link", { size: 13 }), linkedAbove ? "Supersetted with above" : "Superset with above")
        : null,
      h(
        "div",
        { class: "hstack wrap mt", style: { gap: "12px" } },
        ...targetFields(ex)
      ),
      h(
        "label",
        { class: "dim", style: { display: "flex", flexDirection: "column", gap: "3px", fontSize: "0.72rem", marginTop: "10px" } },
        h("span", { class: "hstack", style: { gap: "5px" } }, icon("play", { size: 12, className: "icon muted" }), "Demo video link (optional)"),
        h("input", { class: "input", type: "url", inputmode: "url", value: ex.video_url || "", placeholder: "YouTube / Vimeo / Bunny link", onchange: (e) => (ex.video_url = e.target.value.trim()) })
      )
    );
  }

  function miniField(label, control) {
    return h("label", { class: "dim", style: { display: "flex", flexDirection: "column", gap: "3px", fontSize: "0.72rem" } }, label, control);
  }

  // Per-metric target inputs: lifting carries reps + weight, bodyweight reps only, mobility a
  // hold time, cardio a distance + time. The session seeder prefills the first set(s) from these.
  function targetFields(ex) {
    const mt = metricOf(ex.metric_type);
    const setsField = miniField("Sets", h("input", { class: "set-input", type: "number", min: "1", value: ex.target_sets, style: { width: "56px" }, onchange: (e) => (ex.target_sets = parseInt(e.target.value) || 1) }));
    const restField = miniField("Rest (s)", h("input", { class: "set-input", type: "number", value: ex.rest_seconds ?? "", style: { width: "60px" }, onchange: (e) => (ex.rest_seconds = num(e.target.value)) }));
    const repsField = miniField("Reps", h(
      "span", { class: "hstack", style: { gap: "4px" } },
      h("input", { class: "set-input", type: "number", value: ex.target_reps_min ?? "", style: { width: "48px" }, onchange: (e) => (ex.target_reps_min = num(e.target.value)) }),
      h("span", { class: "dim" }, "–"),
      h("input", { class: "set-input", type: "number", value: ex.target_reps_max ?? "", style: { width: "48px" }, onchange: (e) => (ex.target_reps_max = num(e.target.value)) })
    ));
    const weightField = miniField(`Target (${weightLabel()})`, h("input", { class: "set-input", type: "number", step: "0.5", value: ex.target_weight_kg != null ? toDisplayWeight(ex.target_weight_kg) : "", style: { width: "64px" }, placeholder: "—", onchange: (e) => (ex.target_weight_kg = toStorageKg(e.target.value)) }));
    const timeField = miniField("Target time", h("input", { class: "set-input", type: "text", inputmode: "numeric", placeholder: "m:ss", value: fmtTimeShort(ex.target_duration_seconds), style: { width: "70px" }, onchange: (e) => (ex.target_duration_seconds = parseTimeInput(e.target.value)) }));
    const distField = miniField(`Target (${distanceUnit()})`, h("input", { class: "set-input", type: "number", step: "0.01", placeholder: "—", value: ex.target_distance_m != null ? toDisplayDistance(ex.target_distance_m) : "", style: { width: "70px" }, onchange: (e) => (ex.target_distance_m = toStorageDistanceM(e.target.value)) }));

    if (mt === "duration") return [setsField, timeField, restField];
    if (mt === "distance_duration") return [setsField, distField, timeField, restField];
    if (mt === "bodyweight_reps") return [setsField, repsField, restField];
    return [setsField, repsField, restField, weightField]; // weight_reps
  }

  function num(v) {
    return v === "" ? null : parseInt(v, 10);
  }

  function move(index, dir) {
    const target = index + dir;
    if (target < 0 || target >= selected.length) return;
    [selected[index], selected[target]] = [selected[target], selected[index]];
    render();
  }

  function addExercises() {
    openExercisePicker({
      title: "Add exercises",
      multi: true,
      existing: selected.map((s) => s.library_exercise_id),
      onPick: (lib) => {
        if (selected.some((s) => s.library_exercise_id === lib.id)) return false;
        selected.push(fromTemplate({ ...lib, library_exercise_id: lib.id }));
        render();
        return true;
      },
    });
  }

  async function save() {
    if (!name.trim()) return toast("Give your workout a name", "warn");
    if (!selected.length) return toast("Add at least one exercise", "warn");
    // Drop "supersets of one" — a group needs ≥2 members to mean anything.
    const groupCounts = {};
    for (const e of selected) if (e.superset_group != null) groupCounts[e.superset_group] = (groupCounts[e.superset_group] || 0) + 1;
    const payload = {
      name: name.trim(),
      notes: notes.trim() || null,
      exercises: selected.map((e, i) => ({
        library_exercise_id: e.library_exercise_id,
        position: i,
        target_sets: e.target_sets,
        target_reps_min: e.target_reps_min,
        target_reps_max: e.target_reps_max,
        target_weight_kg: e.target_weight_kg,
        target_duration_seconds: e.target_duration_seconds,
        target_distance_m: e.target_distance_m,
        rest_seconds: e.rest_seconds,
        video_url: e.video_url?.trim() || null,
        superset_group: e.superset_group != null && groupCounts[e.superset_group] >= 2 ? e.superset_group : null,
      })),
    };
    try {
      if (isEdit) await api.updateWorkout(params.id, payload);
      else await api.createWorkout(payload);
      toast(isEdit ? "Workout saved" : "Workout created", "success");
      navigate("/workouts");
    } catch (e) {
      toast(e.message, "error");
    }
  }

  return root;
}
